import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Icon, type ValidIcons } from '@kizen/kds/Icon';
import { CollapseContainer } from '@kizen/kds/InformationTree';
import { Typography } from '@kizen/kds/Typography';
import Loader from '__components/Kizen/Loader';
import { PLUGINS } from '__queries/query-keys';
import PluginService from '__services/PluginService';
import {
  getBusinessClientObject,
  getSettingsAccess,
  selectCanEditTeamMemebersFields,
} from 'store/authentication/selectors';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import type {
  BusinessAppAsset,
  BusinessAppAssetType,
} from 'ts-components/Plugins/businessApps/types';
import { ASSET_TYPES, type SettingsLinkAccess } from './dependencyLinks';

type DependencyGroupProps = {
  assetType: BusinessAppAssetType;
  label: string;
  icon: ValidIcons;
  items: { id: string; name: string; href?: string }[];
};

const DependencyGroup: FC<DependencyGroupProps> = ({
  assetType,
  label,
  icon,
  items,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex items-start gap-[15px]">
      <Icon icon={icon} size="md" />
      <div className="flex flex-col min-w-0">
        <button
          type="button"
          className="appearance-none bg-transparent border-0 p-0 cursor-pointer flex items-center gap-[10px] focus:outline-none focus-visible:outline focus-visible:outline-1"
          onClick={() => setExpanded((x) => !x)}
          aria-expanded={expanded}
          data-qa-id={`business-app-dependencies-${assetType.replace(/_/g, '-')}-toggle`}
        >
          <Typography weight="semibold">{`${label} (${items.length})`}</Typography>
          <Icon
            icon="custom-chevron-down"
            size="sm"
            tooltipEnabled={false}
            className={
              expanded
                ? 'transition-transform duration-300'
                : 'transition-transform duration-300 [transform:rotate(-90deg)]'
            }
          />
        </button>
        <CollapseContainer isExpanded={expanded}>
          <div className="flex flex-col gap-[10px] pb-[4px]">
            {items.map(({ id, name, href }) =>
              href ? (
                <Typography
                  key={id}
                  variant="link"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  qa={{ id: `business-app-dependency-link-${id}` }}
                >
                  {name}
                </Typography>
              ) : (
                <Typography key={id}>{name}</Typography>
              )
            )}
          </div>
        </CollapseContainer>
      </div>
    </div>
  );
};

export const ComponentDependencies: FC<{ appId: string }> = ({ appId }) => {
  const { t } = useTranslation();
  const clientObject = useSelector(getBusinessClientObject);
  const settings = useSelector(getSettingsAccess);
  const canEditTeamMemberFields = useSelector(selectCanEditTeamMemebersFields);
  const settingsAccess = useMemo(
    (): SettingsLinkAccess => ({ ...settings, canEditTeamMemberFields }),
    [settings, canEditTeamMemberFields]
  );
  const {
    data: assets = EMPTY_ARRAY,
    isLoading,
    isError,
  } = useQuery<BusinessAppAsset[]>({
    queryKey: PLUGINS.GET_BUSINESS_APP_ASSETS(appId),
    queryFn: () => PluginService.getAssets(appId),
    throwOnError: false,
  });

  const groups = useMemo(() => {
    return ASSET_TYPES.map(([assetType, config]) => ({
      assetType,
      label: config.label(t),
      icon: config.icon,
      items: assets
        .filter((asset) => asset.asset_type === assetType)
        .map((asset) => ({
          id: asset.id,
          name: asset.name,
          href:
            !config.canView || config.canView(settingsAccess)
              ? config.href(asset, clientObject?.id)
              : undefined,
        })),
    })).filter(({ items }) => items.length);
  }, [assets, clientObject?.id, settingsAccess, t]);

  return (
    <Loader loading={isLoading} paddingTop={0}>
      {isError ? (
        <Typography>
          {t("Kizen component dependencies couldn't be loaded.")}
        </Typography>
      ) : groups.length ? (
        <div className="grid grid-cols-3 gap-[30px]">
          {groups.map((group) => (
            <DependencyGroup key={group.assetType} {...group} />
          ))}
        </div>
      ) : (
        <Typography>
          {t('This app has no Kizen component dependencies.')}
        </Typography>
      )}
    </Loader>
  );
};
